import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export interface ScienceSection {
  title: string;
  icon: React.ElementType;
  equations: {
    label: string;
    formula: string;
    substitution: string;
    result: string;
    status?: 'pass' | 'warn' | 'fail';
    unit?: string;
  }[];
  warnings?: string[];
}

export function ScienceAccordion({ sections, defaultOpen = false }: { sections: ScienceSection[]; defaultOpen?: boolean }) {
  const [openIdx, setOpenIdx] = useState<number | null>(defaultOpen ? 0 : null);

  return (
    <div className="space-y-3">
      {sections.map((section, i) => {
        const isOpen = openIdx === i;
        const Icon = section.icon;
        return (
          <div key={i} className="overflow-hidden rb-card">
            <button
              onClick={() => setOpenIdx(isOpen ? null : i)}
              className="w-full flex items-center justify-between p-4 hover:bg-rb-elevated transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-sky-400" />
                </div>
                <span className="text-sm font-bold text-white">{section.title}</span>
                {section.warnings && section.warnings.length > 0 && (
                  <span className="text-[10px] font-bold text-rose-400 bg-rose-500/10 px-1.5 py-0.5 rounded">
                    {section.warnings.length} warning{section.warnings.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {isOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {isOpen && (
              <div className="p-4 border-t border-slate-800 space-y-3">
                {section.equations.map((eq, j) => {
                  const statusColor = eq.status === 'pass' ? 'text-emerald-400' : eq.status === 'warn' ? 'text-amber-400' : eq.status === 'fail' ? 'text-rose-400' : 'text-slate-400';
                  return (
                    <div key={j} className="p-3 space-y-2 rb-inner-card-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-300">{eq.label}</span>
                        <span className={`text-xs font-bold ${statusColor}`}>{eq.result} {eq.unit}</span>
                      </div>
                      <div className="font-mono text-[11px] text-slate-500 bg-rb-surface rounded p-2">{eq.formula}</div>
                      <div className="font-mono text-[11px] text-sky-400 bg-rb-surface rounded p-2">{eq.substitution}</div>
                    </div>
                  );
                })}
                {section.warnings && section.warnings.map((w, k) => (
                  <div key={k} className="text-xs p-2 rounded-lg bg-rose-500/5 border border-rose-500/20 text-rose-400">
                    {w}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
